import { Stack } from '@mui/joy';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { useGetExtractorVendorMappings } from 'companySettings/hooks';
import {
  canAddExtractorVendorMapping,
  canEditExtractorVendorMapping,
  canRemoveExtractorVendorMapping,
} from 'companySettings/utils';
import { PageTitle } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { defaultShouldRowBeUpdated } from 'core/utils';

import { getColumnDef } from './columns';
import { ExtractorVendorMappingTable } from './ExtractorVendorMappingTable';
import { NoRowsOverlay } from './NoRowsOverlay';

export const ExtractorVendorMapping = () => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const canEdit = canEditExtractorVendorMapping(user);
  const canRemove = canRemoveExtractorVendorMapping(user);

  const columnDefs = useMemo(() => getColumnDef({ canEdit, canRemove }), [canEdit, canRemove]);

  const { records, isError, isLoading } = useGetExtractorVendorMappings();

  // `updated_at` has second granularity, so two saves inside the same second do not look newer to
  // the default row diff. Compare the two editable targets as well, or an edited row stays stale.
  const shouldRowBeUpdated = useCallback(
    (newRow: AgGridDataRow, existingRow: AgGridDataRow) =>
      defaultShouldRowBeUpdated(newRow, existingRow) ||
      newRow?.vendor?.contactGroupAddressId !== existingRow?.vendor?.contactGroupAddressId ||
      newRow?.vendorContact?.contactId !== existingRow?.vendorContact?.contactId,
    []
  );

  // The gap restates the one `PageLayout`'s content wrapper puts between a page title and its list
  // on a top-level list page — this one hangs off the settings layout instead, so it never gets it.
  return (
    <Stack sx={{ gap: 2, height: '100%' }}>
      <PageTitle title={t('titleExtractorVendorMapping')} />

      <SelectionCountProvider>
        <AgGridClientTableProvider
          tableId="extractorVendorMappingsTable"
          entityType="ExtractorVendorMapping"
          columnDefs={columnDefs}
          data={records}
          isDataLoading={isLoading}
          // The list arrives in a single request, so there is no page progress to report: the grid
          // is either still loading its one response or done with it.
          loadingPercentage={isLoading ? 0 : 100}
          dataCurrentPage={isLoading ? 0 : 1}
          shouldRowBeUpdated={shouldRowBeUpdated}
          noRowsOverlayComponent={NoRowsOverlay}
          // A failed request also leaves the grid with no rows; without this the overlay would
          // claim the company has no mappings.
          noRowsOverlayComponentParams={{ isError }}
          hasRowSelection={canRemove}
          headerCheckbox={canRemove}
          suppressCellFocus
          layoutType={TableLayoutType.EXTRACTOR_VENDOR_MAPPING}
          hasLayoutActions
        >
          <ExtractorVendorMappingTable
            canAdd={canAddExtractorVendorMapping(user)}
            canRemove={canRemove}
          />
        </AgGridClientTableProvider>
      </SelectionCountProvider>
    </Stack>
  );
};
